import {
  emptyLayout, sanitizeLayout, LAYOUT_KIND, LAYOUT_LIMITS,
  type BlockSize, type LayoutDoc, type NavLayout
} from '@jyfk/settings/layout';

/**
 * 对一份布局做的每一种改动。**全部是纯函数**：进来一份、出去新的一份，不改原来的。
 *
 * 不改原来的那份是「撤销」能成立的前提 —— 撤销就是把改之前那份原样放回去。
 */

/** 一次写好几个容器的顺序（跨栏拖动时两栏一起变） */
export function withOrders(d: LayoutDoc, orders: Readonly<Record<string, readonly string[]>>): LayoutDoc {
  const order = { ...d.order };
  for (const [zone, ids] of Object.entries(orders)) order[zone] = ids.slice(0, LAYOUT_LIMITS.idsPerZone);
  return { ...d, order };
}

export function withFlag(d: LayoutDoc, which: 'hide' | 'coll', key: string, on: boolean): LayoutDoc {
  const m = { ...d[which] };
  if (on) m[key] = true;
  else delete m[key];
  return { ...d, [which]: m };
}

/** 宽度。传 null = 回到这一块的默认宽度 */
export function withSize(d: LayoutDoc, key: string, size: BlockSize | null): LayoutDoc {
  const m = { ...d.size };
  if (size === null) delete m[key];
  else m[key] = size;
  return { ...d, size: m };
}

export function withLock(d: LayoutDoc, lock: boolean): LayoutDoc {
  return { ...d, lock };
}

export function withTip(d: LayoutDoc, tip: number): LayoutDoc {
  return { ...d, tip: Math.max(0, Math.min(LAYOUT_LIMITS.tip, Math.round(tip))) };
}

export function withNav(d: LayoutDoc, nav: NavLayout): LayoutDoc {
  return { ...d, nav: { order: nav.order.slice(), hide: { ...nav.hide }, tabs: nav.tabs.slice() } };
}

/**
 * 恢复默认排列：顺序、宽度、隐藏、折叠一起清掉（照原型的 clearAll）。
 * **锁定、侧栏导航、提示次数不动** —— 那是另外几件事，各有各的「恢复默认」。
 */
export function resetBlocks(d: LayoutDoc): LayoutDoc {
  return { ...d, order: {}, size: {}, hide: {}, coll: {} };
}

export function resetNav(d: LayoutDoc): LayoutDoc {
  return { ...d, nav: emptyLayout().nav };
}

export function hiddenCount(d: LayoutDoc): number {
  return Object.keys(d.hide).length;
}

/** 调过顺序的容器有几个（外观页「已自定义的区域」那一格） */
export function customizedZones(d: LayoutDoc): number {
  return Object.keys(d.order).length;
}

/** 导出成一个文件：换电脑前留一份、分享给朋友、调乱了想回到上周的样子 */
export function exportFile(d: LayoutDoc, now: Date): string {
  return JSON.stringify({ kind: LAYOUT_KIND, v: 1, at: now.toISOString(), layout: d }, null, 2);
}

export type ImportResult = { ok: true; doc: LayoutDoc } | { ok: false; reason: 'not_json' | 'not_layout' };

/**
 * 读一个导入的文件。**只认我们自己导出的格式**（带 kind），
 * 随便拖一个 JSON 进来不会把布局清空。认得的条目留下、认不得的扔掉（sanitizeLayout）。
 */
export function importFile(text: string): ImportResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'not_json' };
  }
  if (raw === null || typeof raw !== 'object' || (raw as { kind?: unknown }).kind !== LAYOUT_KIND) {
    return { ok: false, reason: 'not_layout' };
  }
  return { ok: true, doc: sanitizeLayout(raw) };
}

/**
 * 两份布局是不是一样（决定要不要存）。
 *
 * **比的是内容，不是键的先后。** Postgres 的 jsonb 会把对象的键重新排序（先短后长），
 * 从服务器读回来的那份和网页上拼出来的那份，字面上的键顺序可能不一样 ——
 * 直接比 JSON 字符串的话，一份没改过的布局也会被当成「改了」再存一遍。
 * 上线自检里就是这么撞见的。数组的顺序照比（那是排列本身）。
 */
export function sameLayout(a: LayoutDoc, b: LayoutDoc): boolean {
  return canonical(a) === canonical(b);
}

function canonical(v: unknown): string {
  if (Array.isArray(v)) return '[' + v.map(canonical).join(',') + ']';
  if (v !== null && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return '{' + Object.keys(o).sort().map(k => JSON.stringify(k) + ':' + canonical(o[k])).join(',') + '}';
  }
  return JSON.stringify(v);
}
